import {useCallback, useEffect, useState} from 'react'
import {BASE_URL} from '../../../api'
import config from '../../config'
import uploadFileSecurely from '../../helper/secureUpload'
import ImageCropModal, {PHOTO_SHAPES} from '../_shared/ImageCropModal'
const {axios} = config

// Roadmap #28/#29 (2026-09-24): manage the app's categories - names in
// English/Spanish, tile images, order, hide/add subcategories. The app
// (2.39+) builds its home and discover tiles from this.

const shrinkImage = (file, maxW = 1000) =>
  new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      const scale = Math.min(1, maxW / img.width)
      const c = document.createElement('canvas')
      c.width = Math.round(img.width * scale)
      c.height = Math.round(img.height * scale)
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height)
      c.toBlob((b) => resolve(b ? new File([b], file.name.replace(/\.\w+$/, '') + '.jpg', {type: 'image/jpeg'}) : file), 'image/jpeg', 0.82)
    }
    img.onerror = () => resolve(file)
    img.src = URL.createObjectURL(file)
  })

// shape: 'tile' (subcategory tiles) or 'banner' (main category image, e.g.
// the Retreats & Experiences banner on Home). New uploads and "Reposition"
// go through the Position photo tool so the subject isn't cropped off.
const ImagePicker = ({value, onChange, busy, setBusy, setError, shape = 'tile'}) => {
  const [cropSource, setCropSource] = useState(null)
  const pick = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setCropSource(file)
  }
  const onCropped = async (file) => {
    setBusy(true)
    try {
      const {fileUrl} = await uploadFileSecurely(await shrinkImage(file, PHOTO_SHAPES[shape].outputWidth))
      onChange(fileUrl)
    } catch (err) {
      setError('Could not upload the image.')
    } finally {
      setCropSource(null)
      setBusy(false)
    }
  }
  const previewW = 64
  const previewH = Math.round(previewW / PHOTO_SHAPES[shape].aspect)
  return (
    <div className='d-flex align-items-center gap-2'>
      {value ? (
        <img src={value} alt='' className='rounded' style={{width: previewW, height: previewH, objectFit: 'cover'}} />
      ) : (
        <div className='rounded bg-light d-flex align-items-center justify-content-center text-muted fs-8' style={{width: previewW, height: Math.max(previewH, 30)}}>
          App default
        </div>
      )}
      <label className='btn btn-sm btn-light mb-0'>
        {busy ? '…' : 'Upload'}
        <input type='file' accept='image/*' hidden disabled={busy} onChange={pick} />
      </label>
      {value && (
        <button type='button' className='btn btn-sm btn-light' disabled={busy} onClick={() => setCropSource(value)}>
          Reposition
        </button>
      )}
      {value && (
        <button type='button' className='btn btn-sm btn-light' onClick={() => onChange('')}>
          Use default
        </button>
      )}
      {cropSource && (
        <ImageCropModal source={cropSource} shape={shape} onCancel={() => setCropSource(null)} onDone={onCropped} />
      )}
    </div>
  )
}

const SubRow = ({s, index, total, onMove, onSaved, setError}) => {
  const [name, setName] = useState(s.name)
  const [nameEs, setNameEs] = useState(s.nameEs || '')
  const [image, setImage] = useState(s.image || '')
  // What ONE person/business here is called, shown under their name in
  // the app's lists ("Massage Therapist", "Gym").
  const [title, setTitle] = useState(s.title || '')
  const [titleEs, setTitleEs] = useState(s.titleEs || '')
  const [busy, setBusy] = useState(false)
  const [uploading, setUploading] = useState(false)
  const dirty =
    name !== s.name ||
    nameEs !== (s.nameEs || '') ||
    image !== (s.image || '') ||
    title !== (s.title || '') ||
    titleEs !== (s.titleEs || '')

  const save = async (extra = {}) => {
    if (name !== s.name && (s.practitioners > 0) &&
      !window.confirm('Renaming the English name: people on app 2.38 or older may not find this subcategory from their home screen until they update. Continue?')) return
    setBusy(true)
    try {
      const body = {name, nameEs, image, ...extra}
      if (title !== (s.title || '')) body.title = title
      if (titleEs !== (s.titleEs || '') || title !== (s.title || '')) body.titleEs = titleEs !== (s.titleEs || '') ? titleEs : undefined
      await axios.put(`${BASE_URL}/admin/subcategory/${s._id}`, body)
      onSaved()
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not save.')
    } finally {
      setBusy(false)
    }
  }
  const remove = async () => {
    if (!window.confirm(`Delete "${s.name}"? This can't be undone.`)) return
    try {
      await axios.delete(`${BASE_URL}/admin/subcategory/${s._id}`)
      onSaved()
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not delete.')
    }
  }

  return (
    <tr className={s.hidden ? 'opacity-50' : ''}>
      <td className='text-nowrap'>
        <button className='btn btn-sm btn-icon btn-light me-1' disabled={index === 0} onClick={() => onMove(index, -1)}>↑</button>
        <button className='btn btn-sm btn-icon btn-light' disabled={index === total - 1} onClick={() => onMove(index, 1)}>↓</button>
      </td>
      <td>
        <input className='form-control form-control-sm form-control-solid' value={name} onChange={(e) => setName(e.target.value)} />
        <input
          className='form-control form-control-sm form-control-solid mt-1 fs-7'
          value={title}
          placeholder='Title for one (e.g. Massage Therapist)'
          title='Shown under each name in the app lists'
          onChange={(e) => setTitle(e.target.value)}
        />
      </td>
      <td>
        <input className='form-control form-control-sm form-control-solid' value={nameEs} placeholder='(auto-translate)' onChange={(e) => setNameEs(e.target.value)} />
        <input
          className='form-control form-control-sm form-control-solid mt-1 fs-7'
          value={titleEs}
          placeholder='Título (auto)'
          onChange={(e) => setTitleEs(e.target.value)}
        />
      </td>
      <td>
        <ImagePicker value={image} onChange={setImage} busy={uploading} setBusy={setUploading} setError={setError} />
      </td>
      <td className='text-muted fs-7 text-nowrap'>
        {s.practitioners} profiles
        <br />
        {s.upcomingClasses} upcoming classes
      </td>
      <td className='text-end text-nowrap'>
        {dirty && (
          <button className='btn btn-sm btn-primary me-2' disabled={busy} onClick={() => save()}>
            Save
          </button>
        )}
        <button className='btn btn-sm btn-light me-2' disabled={busy} onClick={() => save({hidden: !s.hidden})}>
          {s.hidden ? 'Show' : 'Hide'}
        </button>
        {!s.practitioners && !s.upcomingClasses && (
          <button className='btn btn-sm btn-light-danger' onClick={remove}>
            Delete
          </button>
        )}
      </td>
    </tr>
  )
}

const CategoryCard = ({c, reload, setError}) => {
  const [name, setName] = useState(c.name)
  const [nameEs, setNameEs] = useState(c.nameEs || '')
  const [image, setImage] = useState(c.image || '')
  const [uploading, setUploading] = useState(false)
  const [newSub, setNewSub] = useState({name: '', nameEs: ''})
  const [subs, setSubs] = useState(c.subcategories)
  useEffect(() => setSubs(c.subcategories), [c])
  const dirty = name !== c.name || nameEs !== (c.nameEs || '') || image !== (c.image || '')

  const saveCat = async () => {
    try {
      await axios.put(`${BASE_URL}/admin/category-tree/${c._id}`, {name, nameEs, image})
      reload()
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not save.')
    }
  }
  const move = async (i, dir) => {
    const next = [...subs]
    const [x] = next.splice(i, 1)
    next.splice(i + dir, 0, x)
    setSubs(next)
    try {
      await axios.put(`${BASE_URL}/admin/category-tree/${c._id}/order`, {ids: next.map((s) => s._id)})
    } catch (e) {
      setError('Could not save the order.')
      reload()
    }
  }
  const add = async () => {
    if (!newSub.name.trim()) return
    try {
      await axios.post(`${BASE_URL}/admin/category-tree/${c._id}/subcategories`, newSub)
      setNewSub({name: '', nameEs: ''})
      reload()
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not add.')
    }
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-6 align-items-center'>
        <div className='d-flex gap-3 align-items-center flex-wrap'>
          <input className='form-control form-control-solid fw-bold w-275px' value={name} onChange={(e) => setName(e.target.value)} />
          <input className='form-control form-control-solid w-275px' value={nameEs} placeholder='Español' onChange={(e) => setNameEs(e.target.value)} />
          <ImagePicker shape='banner' value={image} onChange={setImage} busy={uploading} setBusy={setUploading} setError={setError} />
          {dirty && (
            <button className='btn btn-sm btn-primary' onClick={saveCat}>
              Save
            </button>
          )}
        </div>
      </div>
      <div className='card-body border-top p-6'>
        <div className='table-responsive'>
          <table className='table align-middle table-row-dashed fs-6 gy-3'>
            <thead>
              <tr className='text-start text-muted fw-bolder fs-7 text-uppercase'>
                <th>Order</th>
                <th>English (name / title for one)</th>
                <th>Español (nombre / título)</th>
                <th>Tile image</th>
                <th>Used by</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s, i) => (
                <SubRow key={s._id} s={s} index={i} total={subs.length} onMove={move} onSaved={reload} setError={setError} />
              ))}
              <tr>
                <td></td>
                <td>
                  <input className='form-control form-control-sm form-control-solid' placeholder='New subcategory' value={newSub.name} onChange={(e) => setNewSub({...newSub, name: e.target.value})} />
                </td>
                <td>
                  <input className='form-control form-control-sm form-control-solid' placeholder='(auto-translate)' value={newSub.nameEs} onChange={(e) => setNewSub({...newSub, nameEs: e.target.value})} />
                </td>
                <td colSpan={2} className='text-muted fs-7'>Add an image after creating it.</td>
                <td className='text-end'>
                  <button className='btn btn-sm btn-light-primary' disabled={!newSub.name.trim()} onClick={add}>
                    + Add
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

const Categories = () => {
  const [cats, setCats] = useState([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setError('')
    try {
      const {data} = await axios.get(`${BASE_URL}/admin/category-tree`)
      setCats(data.data || [])
    } catch (e) {
      setError('Could not load categories.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return (
    <>
      <div className='card mb-5'>
        <div className='card-body p-9'>
          <h3 className='fw-bolder mb-3'>Categories</h3>
          <p className='text-muted mb-0'>
            Names, tile images and order of the categories people browse in the app. Leave Español
            blank to auto-translate. <b>Hide</b> takes a subcategory out of the app without touching
            profiles or classes already tagged with it; <b>Delete</b> only appears when nothing uses it.
            Changes show in the app (2.39+) the next time the home screen loads. Uploading or
            choosing <b>Reposition</b> opens a tool to drag and zoom the photo into the exact shape
            the app shows. A category's own image is used as its Home banner (e.g. Retreats &amp;
            Experiences); subcategory images are the tiles.
          </p>
          {error && <div className='alert alert-danger py-3 mt-4 mb-0'>{error}</div>}
        </div>
      </div>
      {loading ? <div className='text-muted'>Loading…</div> : cats.map((c) => <CategoryCard key={c._id} c={c} reload={load} setError={setError} />)}
    </>
  )
}

export default Categories
